import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, Bot as BotIcon, MoreVertical, Trash2, Copy, X, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { api } from '../api/client';
import { BotFlowEditor, type BotFlow } from '../components/BotFlowEditor';

type BotStatus = 'draft' | 'deployed' | 'paused' | 'error';
type BotType = 'support' | 'sales' | 'faq' | 'lead' | 'generic';

interface BotItem {
  id: string;
  name: string;
  type: BotType;
  status: BotStatus;
  lastActivityAt: string | null;
  flow?: BotFlow;
}

const STATUS_COLORS: Record<BotStatus, string> = {
  draft: 'bg-slate-200 text-slate-700',
  deployed: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700',
};

/** Returns true for errors that mean "no bots yet" rather than a real failure */
function isEmptyStateError(err: unknown): boolean {
  const status = (err as any)?.response?.status as number | undefined;
  return status === 404 || status === 502;
}

export function Bots(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);

  const q = useQuery<BotItem[]>({
    queryKey: ['bots'],
    queryFn: async () => (await api.get<BotItem[]>('/bots')).data,
    // Do not retry on 404/502 — treat them as "not yet provisioned"
    retry: (failureCount, err) => {
      if (isEmptyStateError(err)) return false;
      return failureCount < 3;
    },
  });

  const del = useMutation({
    mutationFn: async (id: string) => api.delete(`/bots/${id}`),
    onSuccess: () => {
      toast.success(t('bots.deleted'));
      void qc.invalidateQueries({ queryKey: ['bots'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const filtered = useMemo(() => {
    const list = q.data ?? [];
    if (!search.trim()) return list;
    const s = search.toLowerCase();
    return list.filter((b) => b.name.toLowerCase().includes(s));
  }, [q.data, search]);

  if (selectedBotId) {
    const bot = (q.data ?? []).find((b) => b.id === selectedBotId);
    return (
      <BotFlowEditor
        botId={selectedBotId}
        initialFlow={bot?.flow}
        onBack={() => setSelectedBotId(null)}
      />
    );
  }

  // Determine whether to show the empty state vs. the real error card.
  // 404/502 from /bots means the service isn't up yet or the route doesn't exist —
  // show a friendly "Create your first bot" prompt instead of a scary red error.
  const showEmptyState =
    q.isError && isEmptyStateError(q.error)
      ? true
      : !q.isError && !q.isLoading && filtered.length === 0;

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-brand-dark">{t('bots.title')}</h1>
        <button
          className="btn-primary flex items-center gap-2"
          onClick={() => setCreateOpen(true)}
        >
          <Plus size={16} /> {t('bots.create')}
        </button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
        />
        <input
          aria-label="search-bots"
          className="input w-full pl-9"
          placeholder={t('bots.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {q.isLoading ? (
        <div className="card flex items-center justify-center gap-2 text-brand-muted">
          <Loader2 size={16} className="animate-spin" /> {t('common.loading')}
        </div>
      ) : q.isError && !isEmptyStateError(q.error) ? (
        /* Real errors (500, 401, network …) — show generic error card */
        <div className="card text-red-600">{t('common.error')}</div>
      ) : showEmptyState ? (
        /* 404/502 from /bots OR genuinely empty list → friendly empty state */
        <div className="card text-brand-muted text-center py-10 space-y-3">
          <BotIcon size={40} className="mx-auto text-brand-primary/40" />
          <p className="font-medium text-brand-dark">{t('bots.emptyTitle', 'Create your first bot')}</p>
          <p className="text-sm">{t('bots.emptyHint', 'Click the button above to get started.')}</p>
          <button
            className="btn-primary inline-flex items-center gap-2 mx-auto"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={16} /> {t('bots.create')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((b) => (
            <BotCard
              key={b.id}
              bot={b}
              onOpen={() => setSelectedBotId(b.id)}
              onDelete={() => del.mutate(b.id)}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <CreateBotModal
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            setSelectedBotId(id);
          }}
        />
      )}
    </div>
  );
}

function BotCard({
  bot,
  onOpen,
  onDelete,
}: {
  bot: BotItem;
  onOpen: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="card relative" data-testid={`bot-card-${bot.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div
          className="flex items-center gap-2 cursor-pointer flex-1"
          onClick={onOpen}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
        >
          <BotIcon size={20} className="text-brand-primary" />
          <h3 className="font-semibold text-brand-dark">{bot.name}</h3>
        </div>
        <button
          aria-label={`menu-${bot.id}`}
          className="text-brand-muted hover:text-brand-ink"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <MoreVertical size={16} />
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="absolute right-2 top-10 bg-white border border-slate-200 rounded shadow-md z-10 w-40"
          >
            <button
              role="menuitem"
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
              onClick={() => { setMenuOpen(false); onOpen(); }}
            >
              <Copy size={14} /> {t('bots.open')}
            </button>
            <button
              role="menuitem"
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600"
              onClick={() => { setMenuOpen(false); onDelete(); }}
            >
              <Trash2 size={14} /> {t('bots.delete')}
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-medium">
          {t(`bots.type.${bot.type}`)}
        </span>
        <span
          className={clsx(
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
            STATUS_COLORS[bot.status],
          )}
        >
          {t(`bots.status.${bot.status}`)}
        </span>
      </div>

      <div className="text-xs text-brand-muted">
        {t('bots.lastActivity')}:{' '}
        {bot.lastActivityAt ? dayjs(bot.lastActivityAt).format('YYYY-MM-DD HH:mm') : '—'}
      </div>
    </div>
  );
}

function CreateBotModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [type, setType] = useState<BotType>('generic');

  const mut = useMutation({
    mutationFn: async () =>
      (await api.post<BotItem>('/bots', { name, type })).data,
    onSuccess: (data) => {
      toast.success(t('bots.created'));
      void qc.invalidateQueries({ queryKey: ['bots'] });
      onCreated(data.id);
    },
    onError: () => toast.error(t('common.error')),
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!name.trim()) return;
    mut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('bots.create')}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{t('bots.create')}</h3>
          <button onClick={onClose} aria-label="close" className="text-brand-muted hover:text-brand-ink">
            <X size={18} />
          </button>
        </div>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('bots.namePlaceholder')}
            </label>
            <input
              required
              autoFocus
              className="input w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('bots.typePlaceholder')}
            </label>
            <select
              className="input w-full"
              value={type}
              onChange={(e) => setType(e.target.value as BotType)}
            >
              <option value="generic">{t('bots.type.generic')}</option>
              <option value="support">{t('bots.type.support')}</option>
              <option value="sales">{t('bots.type.sales')}</option>
              <option value="faq">{t('bots.type.faq')}</option>
              <option value="lead">{t('bots.type.lead')}</option>
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={mut.isPending}>
              {t('bots.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
